import { User } from '@auth0/auth0-react';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { Controller } from 'lib_ts/enums/routing/controllers';
import { AuthRoutes } from 'lib_ts/enums/routing/routes/auth';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import {
  ILoginPayload,
  ILoginResult,
  ILoginSuccess,
  IResetPasswordPayload,
} from 'lib_ts/interfaces/common/i-login';
import { IServerResponse } from 'lib_ts/interfaces/common/i-server-response';
import { TeamSyncStatusExt } from 'lib_ts/interfaces/i-batrack';
import { IUser } from 'lib_ts/interfaces/i-user';
import { IReassignOptions } from 'lib_ts/interfaces/pitches/i-pitch-list';
import { BaseRESTService, IGenericResponse } from 'services/_base-rest.service';

export class AuthService extends BaseRESTService {
  private static instance: AuthService;
  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }

    return AuthService.instance;
  }

  private constructor() {
    super({
      controller: Controller.Auth,
    });
  }

  /** consumes the reset password code if successful (i.e. reset URL will not be useful anymore) */
  async resetPassword(config: IResetPasswordPayload): Promise<IServerResponse> {
    return await this.post(
      {
        uri: AuthRoutes.ResetPassword,
      },
      config
    );
  }

  async loginAuth0(token: string, user: User): Promise<ILoginResult> {
    return await this.post(
      {
        uri: AuthRoutes.LoginAuth0,
        headers: { Authorization: `Bearer ${token}` },
      },
      user
    );
  }

  async loginLegacy(payload: ILoginPayload): Promise<ILoginResult> {
    return await this.post(
      {
        uri: AuthRoutes.LoginLegacy,
      },
      payload
    );
  }

  /**
   * JSON result: { error: boolean, message: string, success: boolean }
   * @param token
   * @returns boolean describing whether the token is valid according to server
   */
  async checkToken(token: string): Promise<boolean> {
    return await this.post(
      {
        uri: AuthRoutes.CheckToken,
      },
      { token }
    )
      .then((result: { error: any; message: string; success: boolean }) => {
        if (result.error) {
          NotifyHelper.warning({ message_md: result.message });
          console.error(result.error);
        }

        return result.success;
      })
      .catch((error) => {
        NotifyHelper.error({
          message_md:
            'There was a problem checking your token. Please login again.',
        });
        console.error(error);
        return false;
      });
  }

  /** changes what the server is overriding game status results to, use null to clear the override and resume regular function */
  async setGameStatusOverride(override: Nullable<boolean>): Promise<boolean> {
    return await this.put(
      {
        uri: AuthRoutes.SetGameStatusOverride,
      },
      { status: override }
    )
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md:
              result.error ??
              'Encountered an error while setting game status override.',
          });
          return false;
        }

        return result.success;
      })
      .catch((reason) => {
        console.error(reason);
        NotifyHelper.error({
          message_md:
            'Encountered an error while setting game status override.',
        });
        return false;
      });
  }

  /** what the server is overriding game status results to (if anything) */
  async readGameStatusOverride(): Promise<Nullable<boolean>> {
    return await this.get({
      uri: AuthRoutes.ReadGameStatusOverride,
    })
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md:
              result.error ??
              'Encountered an error while getting game status override.',
          });
          return null;
        }

        return result.data as Nullable<boolean>;
      })
      .catch((reason) => {
        console.error(reason);
        NotifyHelper.error({
          message_md:
            'Encountered an error while getting game status override.',
        });
        return null;
      });
  }

  /** changes whether the server is checking game status from true => false or vice-versa */
  async toggleGameStatusChecks(): Promise<boolean | undefined> {
    return await this.get({
      uri: AuthRoutes.ToggleGameStatusChecks,
    })
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md:
              result.error ??
              'Encountered an error while toggling game status check.',
          });
          return undefined;
        }

        return result.data.status as boolean;
      })
      .catch((reason) => {
        console.error(reason);
        NotifyHelper.error({
          message_md: 'Encountered an error while toggling game status check.',
        });
        return undefined;
      });
  }

  /** whether the server is checking game status */
  async getCheckGameStatus(): Promise<boolean | undefined> {
    return await this.get({
      uri: AuthRoutes.ReadGameStatusChecks,
    })
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md:
              result.error ??
              'Encountered an error while getting game status check.',
          });
          return undefined;
        }

        return result.data.status as boolean;
      })
      .catch((reason) => {
        console.error(reason);
        NotifyHelper.error({
          message_md: 'Encountered an error while getting game status check.',
        });
        return undefined;
      });
  }

  /** what the server received from batrack on its most recent call */
  async getGameStatuses(): Promise<TeamSyncStatusExt[]> {
    return await this.get({
      uri: AuthRoutes.ReadGameStatuses,
    })
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md:
              result.error ??
              'Encountered an error while getting game status check.',
          });
          return [];
        }

        return result.data as TeamSyncStatusExt[];
      })
      .catch((reason) => {
        console.error(reason);
        NotifyHelper.error({
          message_md: 'Encountered an error while getting game status check.',
        });
        return [];
      });
  }

  /**
   * which teams, machines, and users should be available for this user when reassigning pitches
   */
  async reassignOptions(): Promise<IReassignOptions> {
    return await this.get({
      uri: AuthRoutes.GetReassignOptions,
    });
  }

  /** gets a new session id (and inserts a login event) without having to actually log out and in again */
  async newSession(): Promise<ILoginSuccess | undefined> {
    return await this.get({
      uri: AuthRoutes.NewSession,
    }).then((result) => {
      if (result.error) {
        NotifyHelper.warning({ message_md: result.message });
        return undefined;
      }

      return result as ILoginSuccess;
    });
  }

  /** simulates a login as any user on behalf of a super admin */
  async startImpersonation(
    payload: Partial<ILoginPayload>
  ): Promise<ILoginResult | undefined> {
    return await this.post(
      {
        uri: AuthRoutes.StartImpersonation,
      },
      payload
    )
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md:
              result.error ?? `Failed to impersonate ${payload.email}.`,
          });
          return undefined;
        }

        return result.data as ILoginResult;
      })
      .catch((error) => {
        console.error(error);
        return undefined;
      });
  }

  /** allows an admin to enter/leave super access mode to load and interact with data from all teams/users */
  async toggleSuperMode(): Promise<ILoginSuccess | undefined> {
    return await this.get({
      uri: AuthRoutes.ToggleSuperMode,
    }).then((result) => {
      if (result.error) {
        NotifyHelper.warning({ message_md: result.message });
        return undefined;
      }

      return result as ILoginSuccess;
    });
  }

  async updateUser(payload: Partial<IUser>): Promise<IUser> {
    return await this.put(
      {
        uri: AuthRoutes.UpdateUser,
      },
      payload
    );
  }

  async refreshAuthToken(): Promise<ILoginSuccess> {
    return await this.get({
      uri: AuthRoutes.RefreshAuthToken,
    });
  }

  async logout(): Promise<IGenericResponse> {
    return await this.get({
      uri: AuthRoutes.Logout,
    });
  }

  /** note that super admins will also be included */
  async getTeamAdmins(): Promise<IUser[]> {
    return await this.get({
      uri: AuthRoutes.GetTeamAdmins,
    });
  }
}
